import { useEffect, useMemo, useRef, useState } from 'react';
import { RARITY_COLOR } from '../../../shared/cosmetics';
import { SEASON_DEFS } from '../../../shared/seasons';
import { apiGet, apiPost, type MeResponse, type PassConfig, type PassTableResponse, type SeasonInfo } from '../game/api';
import { audio } from '../game/audio';
import { css } from '../game/colors';
import { formatDate, formatNumber, t, type Key } from '../i18n';
import { missionLabel, seasonName } from '../i18n/catalog';
import { Amount, GemIcon, Icon } from './icons';
import { PageHead, type Page } from './MenuShell';
import { rarityName } from './PaintPage';
import { RewardArt, cosmeticOf, isMajor, rewardKindLabel, rewardLabel, type PassLevel, type Reward } from './rewards';
import { PriceButton } from './ShopPanel';

/** Barème par défaut, si le serveur n'envoie pas encore `config` (mêmes valeurs que server/src/accounts/pass.ts). */
export const DEFAULT_PASS_CONFIG: PassConfig = { xpPerLevel: 2000, maxLevel: 105, premiumGems: 900 };

/** Tableau public d'une saison : niveaux, barème, dates, saison suivante. */
export interface PassTable {
  seasonId: string;
  levels: PassLevel[];
  config: PassConfig;
  info: SeasonInfo | null;
  next: SeasonInfo | null;
}

/** Saison du calendrier partagé (shared/seasons.ts), ou null. */
export const seasonDef = (id: string | undefined): SeasonInfo | null => SEASON_DEFS.find((s) => s.id === id) ?? null;
/** Saison en cours selon l'horloge locale (repli pour les invités, avant la réponse du serveur). */
export const localSeason = (now = Date.now()): SeasonInfo =>
  SEASON_DEFS.find((s) => s.startsAt <= now && now < s.endsAt) ?? [...SEASON_DEFS].filter((s) => s.startsAt <= now).pop() ?? SEASON_DEFS[0];
/** Numéro d'une saison lu dans son identifiant (« S2 » → 2). */
export const seasonNumber = (id: string): number => Number(/\d+/.exec(id)?.[0] ?? 1);

export function toPassTable(r: PassTableResponse<PassLevel>, asked?: string): PassTable {
  const id = r.season ?? r.seasonInfo?.id ?? asked ?? '';
  return { seasonId: id, levels: r.levels, config: { ...DEFAULT_PASS_CONFIG, ...r.config }, info: r.seasonInfo ?? seasonDef(id), next: r.next ?? null };
}

/** Clé de la saison en cours quand on ne connaît pas son identifiant (invité, profil pas encore chargé). */
const CURRENT = '';
const tables = new Map<string, PassTable>();
const requests = new Map<string, Promise<PassTable>>();

function cachedTable(key: string): PassTable | null {
  const tb = tables.get(key) ?? null;
  // « Saison en cours » mise en cache avant une bascule de saison : on la recharge.
  if (key === CURRENT && tb?.info && tb.info.endsAt <= Date.now() && tb.next && tb.next.startsAt <= Date.now()) return null;
  return tb;
}

function loadTable(key: string): Promise<PassTable> {
  let p = requests.get(key);
  if (!p) {
    p = apiGet<PassTableResponse<PassLevel>>(key ? `/api/pass?season=${encodeURIComponent(key)}` : '/api/pass')
      .then((r) => {
        const tb = toPassTable(r, key || undefined);
        if (tb.seasonId) tables.set(tb.seasonId, tb);
        if (!key) tables.set(CURRENT, tb);
        return tb;
      })
      .finally(() => requests.delete(key));
    requests.set(key, p);
  }
  return p;
}

/**
 * Tableau d'une saison (public, identique pour tous), mis en cache par identifiant de saison : quand le profil passe
 * à la saison suivante, le nouveau tableau est chargé (jamais celui de la saison précédente). Sans identifiant :
 * la saison en cours selon le serveur.
 */
export function usePassTable(seasonId?: string, enabled = true): PassTable | null {
  const key = seasonId ?? CURRENT;
  const [got, setGot] = useState<{ key: string; tb: PassTable } | null>(null);
  useEffect(() => {
    if (!enabled || cachedTable(key)) return;
    let live = true;
    loadTable(key)
      .then((tb) => live && setGot({ key, tb }))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [key, enabled]);
  if (!enabled) return null;
  return cachedTable(key) ?? (got?.key === key ? got.tb : null);
}

/** Niveaux d'une saison (voir usePassTable). */
export function usePassLevels(seasonId?: string): PassLevel[] | null {
  return usePassTable(seasonId)?.levels ?? null;
}

/** Horloge à la minute (compte à rebours) ; `fixed` la fige (planches de démonstration). */
export function useMinuteClock(fixed?: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (fixed !== undefined) return;
    let id = 0;
    const tick = () => {
      setNow(Date.now());
      id = window.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 50);
    };
    id = window.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 50);
    return () => clearTimeout(id);
  }, [fixed]);
  return fixed ?? now;
}

/** « fin dans 12 j 4 h », ou à moins de 24 h « fin dans 5 h 12 min » (arrondi à la minute supérieure). */
export function endsInText(ms: number): string {
  const min = Math.max(0, Math.ceil(ms / 60_000));
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  return min < 1440 ? t('passCountdownHours', { h, m: min % 60 }) : t('passCountdown', { d, h });
}

export function startsInText(ms: number): string {
  const hTotal = Math.max(0, Math.ceil(ms / 3_600_000));
  return t('passStartsIn', { d: Math.floor(hTotal / 24), h: hTotal % 24 });
}

type Track = 'free' | 'premium';
const seenKey = (id: string) => `tankshift.passSeen.${id}`;

function readSeen(id: string): number | null {
  try {
    const v = localStorage.getItem(seenKey(id));
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

function writeSeen(id: string, level: number): void {
  try {
    localStorage.setItem(seenKey(id), String(level));
  } catch {
    /* stockage indisponible : la révélation sera rejouée une fois */
  }
}

/** Libellé court sous la tuile : montant, multiplicateur ou nom de l'objet. */
function shortLabel(r: Reward): string {
  if (r.kind === 'coins' || r.kind === 'gems') return formatNumber(r.amount);
  if (r.kind === 'token') return r.amount > 1 ? `×${r.amount}` : t(`tokenShort_${r.token}` as Key);
  return rewardLabel(r);
}

export function PassPage({
  me,
  onChanged,
  go,
  table: given,
  now: fixedNow,
  demo,
}: {
  me: MeResponse | null;
  onChanged: () => void;
  go: (p: Page) => void;
  /** Tableau fourni directement (planche `?sheet=pass`, sans serveur) ; sinon chargé depuis `/api/pass`. */
  table?: PassTable;
  /** Heure figée du compte à rebours (planche de démonstration). */
  now?: number;
  /** Démonstration : ni révélation des paliers, ni mémoire locale, ni achat. */
  demo?: boolean;
}) {
  const pass = me?.pass;
  const loaded = usePassTable(pass?.season.id, !given);
  const table = given ?? loaded;
  const levels = table?.levels ?? null;
  const { xpPerLevel: XP_PER_LEVEL, maxLevel: MAX_LEVEL, premiumGems: PREMIUM_GEMS } = table?.config ?? DEFAULT_PASS_CONFIG;
  const now = useMinuteClock(fixedNow);
  const account = me?.account;
  const level = pass?.level ?? 0;
  const premium = !!pass?.premium;
  const claimed = useMemo(() => new Set(pass?.claimed ?? []), [pass?.claimed]);
  const inLevel = (pass?.xp ?? 0) % XP_PER_LEVEL;
  const [msg, setMsg] = useState('');
  const [buying, setBuying] = useState(false);
  const [sel, setSel] = useState<{ level: number; track: Track } | null>(null);
  const [reveal, setReveal] = useState<{ from: number; to: number } | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Sélection par défaut : prochaine récompense marquante devant le joueur.
  const current = sel ?? (() => {
    const next = levels?.find((l) => l.level > level && (isMajor(l.free) || isMajor(l.premium)));
    return next ? { level: next.level, track: (isMajor(next.free) ? 'free' : 'premium') as Track } : { level: Math.max(1, level), track: 'free' as Track };
  })();
  const selLevel = levels?.find((l) => l.level === current.level);

  // Paliers franchis depuis la dernière visite : petite révélation, une seule fois.
  useEffect(() => {
    if (!account || !pass || demo) return;
    const seen = readSeen(account.publicId);
    if (seen !== null && pass.level > seen) {
      setReveal({ from: seen + 1, to: pass.level });
      audio.play('uiReward');
    }
    writeSeen(account.publicId, pass.level);
  }, [account?.publicId, pass?.level]);

  // Ouverture : la piste se place sur le niveau atteint (défilement horizontal seul : la page ne bouge pas).
  useEffect(() => {
    const track = trackRef.current;
    const el = track?.querySelector<HTMLElement>(`[data-level="${Math.max(1, level)}"]`);
    if (!track || !el) return;
    const a = el.getBoundingClientRect();
    const b = track.getBoundingClientRect();
    track.scrollBy({ left: a.left + a.width / 2 - (b.left + b.width / 2), behavior: 'instant' });
  }, [levels]);

  // Molette verticale → défilement horizontal de la piste ; glisser à la souris pour la parcourir.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      const pos = Math.abs(el.scrollLeft);
      if ((e.deltaY < 0 && pos <= 0) || (e.deltaY > 0 && pos >= max - 1)) return;
      e.preventDefault();
      el.scrollLeft += getComputedStyle(el).direction === 'rtl' ? -e.deltaY : e.deltaY;
    };
    let drag: { x: number; left: number; moved: boolean } | null = null;
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: el.scrollLeft, moved: false };
    };
    const move = (e: PointerEvent) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > 5) drag.moved = true;
      if (drag.moved) {
        el.scrollLeft = drag.left - dx;
        el.classList.add('dragging');
      }
    };
    const up = () => {
      if (drag?.moved) {
        // Le relâchement après un glisser ne doit pas sélectionner la tuile sous le pointeur.
        const stop = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        el.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 0);
      }
      drag = null;
      el.classList.remove('dragging');
    };
    el.addEventListener('wheel', wheel, { passive: false });
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('wheel', wheel);
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [levels]);

  const page = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const rtl = getComputedStyle(el).direction === 'rtl';
    el.scrollBy({ left: dir * (rtl ? -1 : 1) * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const focusTile = (lv: number, tr: Track) => {
    setSel({ level: lv, track: tr });
    requestAnimationFrame(() => trackRef.current?.querySelector<HTMLElement>(`[data-tile="${lv}-${tr}"]`)?.focus());
  };
  const onTileKey = (e: React.KeyboardEvent, lv: number, tr: Track) => {
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwd && lv < MAX_LEVEL) focusTile(lv + 1, tr);
    else if (e.key === back && lv > 1) focusTile(lv - 1, tr);
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') focusTile(lv, tr === 'free' ? 'premium' : 'free');
    else if (e.key === 'Home') focusTile(1, tr);
    else if (e.key === 'End') focusTile(MAX_LEVEL, tr);
    else return;
    e.preventDefault();
  };

  const buyPremium = async () => {
    if (demo) return;
    setBuying(true);
    const r = await apiPost<{ result?: string; error?: string }>('/api/pass/premium');
    setBuying(false);
    const key = r.data.result ?? r.data.error ?? 'server';
    setMsg(t(`premium_${key}` as Key));
    audio.play(key === 'ok' ? 'uiBuy' : 'uiDeny');
    onChanged();
  };

  const status = (lv: number, tr: Track): 'claimed' | 'reached' | 'locked' | 'ahead' => {
    if (claimed.has(`${lv}:${tr}`)) return 'claimed';
    if (tr === 'premium' && !premium) return 'locked';
    return lv <= level ? 'reached' : 'ahead';
  };
  const statusText = (s: ReturnType<typeof status>, lv: number) =>
    s === 'claimed' ? t('passClaimed') : s === 'locked' ? t('passPremiumNeeded') : s === 'reached' ? t('passReached') : t('passIn', { n: lv - level });

  // Saison affichée : celle du profil, sinon celle du tableau, sinon le calendrier partagé (jamais un nom en dur).
  const info = table?.info ?? seasonDef(pass?.season.id);
  const season: SeasonInfo = {
    ...(info ?? localSeason(now)),
    ...(pass ? { id: pass.season.id, name: pass.season.name, endsAt: pass.season.endsAt } : {}),
    ...(pass?.season.startsAt !== undefined ? { startsAt: pass.season.startsAt } : {}),
  };
  const ended = pass?.ended ?? now >= season.endsAt;
  const upcoming = !ended && now < season.startsAt;
  const next = table?.next ?? null;
  const selReward = selLevel ? selLevel[current.track] : null;
  const selStatus = selLevel ? status(selLevel.level, current.track) : null;
  const selCos = selReward ? cosmeticOf(selReward) : null;
  const gems = me?.balances.gems ?? 0;

  return (
    <div className="page pass-page">
      <PageHead
        title={t('passTitle')}
        sub={
          <>
            <span className="display">{seasonName(season.id, season.name)}</span>
            {' · '}
            {ended ? (
              <>
                {t('passEnded')}
                {next && next.startsAt > now && (
                  <span className="pass-next">
                    {' · '}
                    <span className="display">{seasonName(next.id, next.name)}</span> : {startsInText(next.startsAt - now)}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="pass-period">{t('passPeriod', { start: formatDate(season.startsAt), end: formatDate(season.endsAt, 'datetime') })}</span>
                {' · '}
                <span className={`pass-countdown num ${!upcoming && season.endsAt - now < 86_400_000 ? 'soon' : ''}`} role="timer">
                  {upcoming ? startsInText(season.startsAt - now) : endsInText(season.endsAt - now)}
                </span>
              </>
            )}
          </>
        }
      />

      {reveal && levels && (
        <section className="plate pass-reveal" role="status">
          <span className="stamp display">{t('passNewTitle')}</span>
          <div className="pass-reveal-body">
            <p>{reveal.from === reveal.to ? t('passNewOne', { n: reveal.to }) : t('passNewMany', { a: reveal.from, b: reveal.to })}</p>
            <div className="pass-reveal-list">
              {levels
                .filter((l) => l.level >= reveal.from && l.level <= reveal.to)
                .flatMap((l) => [l.free, ...(premium ? [l.premium] : [])].map((r, i) => ({ r, key: `${l.level}-${i}` })))
                .slice(0, 12)
                .map(({ r, key }) => (
                  <span key={key} className="reveal-item" title={rewardLabel(r)}>
                    <RewardArt r={r} size={40} />
                  </span>
                ))}
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={() => setReveal(null)}>
            <Icon name="close" size={16} />
          </button>
        </section>
      )}

      <section className="pass-hero">
        <div className="plate pass-progress">
          <div className="pass-badge" aria-hidden>
            <span className="display num">{Math.max(1, level)}</span>
          </div>
          <div className="pass-progress-body">
            <span className="eyebrow">{account ? t('passYourLevel') : t('passPreview')}</span>
            <strong className="pass-level-line display num">{account ? t('passLevelOf', { n: Math.max(1, level), max: MAX_LEVEL }) : t('passLevels', { n: MAX_LEVEL })}</strong>
            {pass ? (
              <>
                <div className="bar bar-xp">
                  <div className="fill fill-xp" style={{ width: `${level >= MAX_LEVEL ? 100 : (inLevel / XP_PER_LEVEL) * 100}%` }} />
                  <span>{level >= MAX_LEVEL ? t('evolveMax') : `${formatNumber(inLevel)} / ${formatNumber(XP_PER_LEVEL)} XP`}</span>
                </div>
                <p className="wip">{t('passXpInfo')}</p>
              </>
            ) : (
              <>
                <p className="wip">{t('passGuestInfo')}</p>
                <button type="button" className="btn btn-small" onClick={() => go('profile')}>
                  <Icon name="profile" size={16} />
                  {t('register')}
                </button>
              </>
            )}
          </div>
        </div>
        <div className={`plate pass-premium ${premium ? 'owned' : ''}`}>
          <span className="eyebrow">{t('passPremium')}</span>
          {premium ? (
            <>
              <strong className="display premium-on">
                <Icon name="star" size={18} />
                {t('passPremiumActive')}
              </strong>
              <p className="wip">{t('passPremiumActiveInfo')}</p>
            </>
          ) : (
            <>
              <strong className="premium-price">
                <GemIcon size={22} />
                <span className="display num">{formatNumber(PREMIUM_GEMS)}</span>
              </strong>
              <p className="wip">{t('premiumInfo')}</p>
              {account && (
                <p className="wip num">
                  {t('passBalance')} <Amount kind="gems" n={gems} size={14} />
                </p>
              )}
              {/* Achat en deux temps, comme dans la boutique : 900 gemmes ne partent pas sur un seul clic. */}
              <PriceButton price={{ gems: PREMIUM_GEMS }} disabled={!account || buying || ended || upcoming} onBuy={buyPremium} wide />
            </>
          )}
          {msg && <p className="form-msg">{msg}</p>}
        </div>
      </section>

      <section className="pass-board" aria-label={t('passTrackLabel')}>
        <div className="pass-rails" aria-hidden>
          <span className="pass-rail-label">{t('passFree')}</span>
          <span className="pass-rail-label premium">
            <Icon name="star" size={14} />
            {t('passPremium')}
          </span>
        </div>
        <button type="button" className="icon-btn track-nav prev" aria-label={t('passPrev')} onClick={() => page(-1)}>
          <Icon name="left" />
        </button>
        <div className="pass-track" ref={trackRef}>
          {!levels && <p className="wip">{t('loading')}</p>}
          {levels?.map((l) => {
            const reached = l.level <= level;
            const milestone = l.level % 5 === 0 || isMajor(l.free) || isMajor(l.premium);
            return (
              <div key={l.level} data-level={l.level} className={`pass-level ${reached ? 'reached' : ''} ${l.level === level ? 'current' : ''} ${milestone ? 'milestone' : ''}`}>
                <span className="pass-num num">{l.level === level ? <span className="pass-here">{t('passHere')}</span> : null}{l.level}</span>
                {(['free', 'premium'] as Track[]).map((tr) => {
                  const r = l[tr];
                  const s = status(l.level, tr);
                  const on = current.level === l.level && current.track === tr;
                  return (
                    <button
                      key={tr}
                      type="button"
                      data-tile={`${l.level}-${tr}`}
                      className={`pass-tile ${tr} ${s} ${isMajor(r) ? 'major' : ''} ${on ? 'on' : ''}`}
                      aria-pressed={on}
                      tabIndex={on ? 0 : -1}
                      aria-label={`${t('level', { n: l.level })} · ${tr === 'free' ? t('passFree') : t('passPremium')} : ${rewardLabel(r)} · ${statusText(s, l.level)}`}
                      onClick={() => setSel({ level: l.level, track: tr })}
                      onKeyDown={(e) => onTileKey(e, l.level, tr)}
                    >
                      <RewardArt r={r} size={isMajor(r) ? 46 : 34} />
                      <span className="pass-tile-label">{shortLabel(r)}</span>
                      {s === 'claimed' && (
                        <span className="pass-state ok" aria-hidden>
                          <Icon name="check" size={14} />
                        </span>
                      )}
                      {s === 'locked' && (
                        <span className="pass-state" aria-hidden>
                          <Icon name="lock" size={13} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <button type="button" className="icon-btn track-nav next" aria-label={t('passNext')} onClick={() => page(1)}>
          <Icon name="right" />
        </button>
      </section>

      <div className="pass-lower">
        {selLevel && selReward && selStatus && (
          <section className={`plate pass-detail ${current.track}`} aria-live="polite">
            <div className="pass-detail-art">
              <RewardArt r={selReward} size={104} />
            </div>
            <div className="pass-detail-body">
              <span className="eyebrow">
                {t('level', { n: selLevel.level })} · {current.track === 'free' ? t('passFree') : t('passPremium')} · {rewardKindLabel(selReward)}
                {selCos && (
                  <>
                    {' · '}
                    <span className="rarity-tag" style={{ color: css(RARITY_COLOR[selCos.rarity]) }}>
                      {rarityName(selCos.rarity)}
                    </span>
                  </>
                )}
              </span>
              <h2 className="display">{rewardLabel(selReward)}</h2>
              <p className="wip">{selReward.kind === 'token' ? t(`tokdesc_${selReward.token}` as Key) : selReward.kind === 'item' ? t('rkdesc_item', { s: seasonName(season.id, season.name) }) : t(`rkdesc_${selReward.kind}` as Key)}</p>
              <span className="pass-detail-tags">
                {account && (
                  <span className={`badge ${selStatus === 'claimed' ? 'badge-ok' : selStatus === 'locked' ? 'badge-brass' : 'badge-accent'}`}>
                    <Icon name={selStatus === 'claimed' ? 'check' : selStatus === 'locked' ? 'lock' : 'pass'} size={12} />
                    {statusText(selStatus, selLevel.level)}
                  </span>
                )}
                {selCos && (
                  <button type="button" className="btn btn-small btn-ghost pass-equip" onClick={() => go('paint')}>
                    <Icon name="wrench" size={14} />
                    {t('passItemEquip')}
                  </button>
                )}
              </span>
            </div>
          </section>
        )}
        {me?.missions && (
          <section className="plate missions" aria-labelledby="missions-title">
            <h2 className="plate-title" id="missions-title">
              {t('passEarnTitle')}
            </h2>
            <h3 className="eyebrow">{t('dailyMissions', { c: me.missions.dailyReward.coins, x: me.missions.dailyReward.passXp })}</h3>
            {me.missions.daily.map((m) => (
              <MissionRow key={m.id} m={m} />
            ))}
            <h3 className="eyebrow">{t('weeklyMissions', { c: me.missions.weeklyReward.coins, x: me.missions.weeklyReward.passXp })}</h3>
            {me.missions.weekly.map((m) => (
              <MissionRow key={m.id} m={m} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}

export function MissionRow({ m }: { m: { id: string; label: string; target: number; progress: number; done: boolean } }) {
  return (
    <div className={`mission ${m.done ? 'done' : ''}`}>
      <span className="mission-check" aria-hidden>
        {m.done ? <Icon name="check" size={14} /> : null}
      </span>
      <span className="mission-label">{missionLabel(m.id, m.label)}</span>
      <div className="bar">
        <div className="fill fill-xp" style={{ width: `${Math.min(100, (m.progress / m.target) * 100)}%` }} />
        <span className="num">{m.done ? t('missionDone') : `${formatNumber(m.progress)} / ${formatNumber(m.target)}`}</span>
      </div>
    </div>
  );
}
